import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Toaster } from './components/ui/sonner';
import CookieBanner from './components/CookieBanner';
import HomePage from './pages/HomePage';
import ProcessusPage from './pages/ProcessusPage';
import TarifsPage from './pages/TarifsPage';
import DecollagePage from './pages/DecollagePage';
import RealisationsPage from './pages/RealisationsPage';
import RealisationDetailPage from './pages/RealisationDetailPage';
import LaColabPage from './pages/LaColabPage';
import { MentionsLegales, PolitiqueConfidentialite, CGV } from './pages/LegalPages';
import LocalSEOPage from './pages/seo/LocalSEOPage';
import BourgEnBressePage from './pages/seo/BourgEnBressePage';
import LyonPage from './pages/seo/LyonPage';
import SuissePage from './pages/seo/SuissePage';
import AinPage from './pages/seo/AinPage';
import SavoiePage from './pages/seo/SavoiePage';
import HauteSavoiePage from './pages/seo/HauteSavoiePage';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  
  return null;
}

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Client pages
import ClientLayout from './pages/client/ClientLayout';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientSubscriptions from './pages/client/ClientSubscriptions';
import ClientQuotes from './pages/client/ClientQuotes';
import ClientInvoices from './pages/client/ClientInvoices';
import ClientSettings from './pages/client/ClientSettings';
import PaymentSuccess from './pages/client/PaymentSuccess';

// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminClients from './pages/admin/AdminClients';
import AdminClientDetail from './pages/admin/AdminClientDetail';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminCommercialQuotes from './pages/admin/AdminCommercialQuotes';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminPromoCodes from './pages/admin/AdminPromoCodes';
import AdminProspecting from './pages/admin/AdminProspecting';
import AdminMessages from './pages/admin/AdminMessages';

import './App.css';

// Protected Route Components
function ProtectedRoute({ children, requireAdmin = false }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/client" replace />;
  }
  
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to={isAdmin ? "/admin" : "/client"} replace />;
  }
  
  return children;
}

// Main public layout with navbar/footer
function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1">
        {children}
      </div>
      <Footer />
      <CookieBanner />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public pages with navbar/footer */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/processus" element={<PublicLayout><ProcessusPage /></PublicLayout>} />
      <Route path="/tarifs" element={<PublicLayout><TarifsPage /></PublicLayout>} />
      <Route path="/decollage" element={<PublicLayout><DecollagePage /></PublicLayout>} />
      <Route path="/realisations" element={<PublicLayout><RealisationsPage /></PublicLayout>} />
      <Route path="/realisations/:projectId" element={<PublicLayout><RealisationDetailPage /></PublicLayout>} />
      <Route path="/lacolab" element={<PublicLayout><LaColabPage /></PublicLayout>} />
      <Route path="/mentions-legales" element={<PublicLayout><MentionsLegales /></PublicLayout>} />
      <Route path="/politique-confidentialite" element={<PublicLayout><PolitiqueConfidentialite /></PublicLayout>} />
      <Route path="/cgv" element={<PublicLayout><CGV /></PublicLayout>} />
      
      {/* Local SEO pages */}
      <Route path="/creation-site-web-bourg-en-bresse" element={<PublicLayout><BourgEnBressePage /></PublicLayout>} />
      <Route path="/creation-site-web-lyon" element={<PublicLayout><LyonPage /></PublicLayout>} />
      <Route path="/creation-site-web-suisse" element={<PublicLayout><SuissePage /></PublicLayout>} />
      <Route path="/creation-site-web-ain" element={<PublicLayout><AinPage /></PublicLayout>} />
      <Route path="/creation-site-web-savoie" element={<PublicLayout><SavoiePage /></PublicLayout>} />
      <Route path="/creation-site-web-haute-savoie" element={<PublicLayout><HauteSavoiePage /></PublicLayout>} />
      
      {/* Auth pages (no navbar/footer) */}
      <Route path="/auth/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/auth/register" element={<Navigate to="/auth/login" replace />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Client dashboard */}
      <Route path="/client" element={<ProtectedRoute><ClientLayout /></ProtectedRoute>}>
        <Route index element={<ClientDashboard />} />
        <Route path="subscriptions" element={<ClientSubscriptions />} />
        <Route path="quotes" element={<ClientQuotes />} />
        <Route path="invoices" element={<ClientInvoices />} />
        <Route path="settings" element={<ClientSettings />} />
        <Route path="payment-success" element={<PaymentSuccess />} />
      </Route>
      
      {/* Admin dashboard */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:clientId" element={<AdminClientDetail />} />
        <Route path="quotes" element={<AdminQuotes />} />
        <Route path="commercial-quotes" element={<AdminCommercialQuotes />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="promo-codes" element={<AdminPromoCodes />} />
        <Route path="prospecting" element={<AdminProspecting />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
